import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './dashboard.module.css';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  FileText,
  MapPin,
  Package,
  Plus,
  Truck,
  User,
  Users,
} from 'react-feather';
import moment from 'moment';
import { authData, withAdminAuth } from '../../components/GetAuth';
import PageHeader from '../../components/PageHeader';
import { useWebsite } from '../../../context/WebsiteContext';
import { useSiteBranding } from '../../../hooks/useSiteBranding';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// One entry per booking status, in flow order. `doughnut_data` from the API
// follows this same order (0,1,2,3,4,9,5,6,7), so index i maps to STAGE_META[i].
const STAGE_META = [
  { status: 0, label: 'Booked', region: 'uk' },
  { status: 1, label: 'Assigned to driver', region: 'uk' },
  { status: 2, label: 'Picked up', region: 'uk' },
  { status: 3, label: 'In UK warehouse', region: 'uk' },
  { status: 4, label: 'Shipped', region: 'uk' },
  { status: 9, label: 'Received at port', region: 'gh' },
  { status: 5, label: 'Ghana warehouse', region: 'gh' },
  { status: 6, label: 'Out for delivery', region: 'gh' },
  { status: 7, label: 'Delivered', region: 'gh' },
];

const SHIPMENT_STATUS = {
  0: { label: 'Loading', tone: 'Orange' },
  1: { label: 'In transit', tone: 'Blue' },
  4: { label: 'At port', tone: 'Teal' },
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const gbpCompact = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  notation: 'compact',
  maximumFractionDigits: 1,
});

function toNumber(value) {
  const n = parseFloat(String(value ?? 0).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(value) {
  return gbp.format(toNumber(value));
}

function formatCompactGBP(value) {
  const n = toNumber(value);
  return n >= 10000 ? gbpCompact.format(n) : gbp.format(n);
}

function stageFor(status) {
  return STAGE_META.find((s) => String(s.status) === String(status));
}

function greeting() {
  const hour = moment().hour();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// Simple block-wise skeleton primitives -- each dashboard section decides
// for itself whether to render its skeleton or its real content.
function SkeletonLine({ width }) {
  return <span className={styles.SkeletonLine} style={width ? { '--sk-w': width } : undefined} />;
}

function SkeletonBlock({ className }) {
  return <div className={`${styles.SkeletonBlock} ${className || ''}`} />;
}

function SkeletonRows({ count = 4 }) {
  return (
    <div className={styles.SkeletonTable}>
      {Array.from({ length: count }).map((_, row) => (
        <div className={styles.SkeletonTableRow} key={row}>
          <SkeletonLine width="35%" />
          <SkeletonLine width="20%" />
        </div>
      ))}
    </div>
  );
}

// Tracks a media query so blocks can swap widgets at the breakpoint
// instead of squeezing desktop layouts into a phone width.
function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function TrendPill({ current, previous, suffix = 'vs last month', short }) {
  if (current === undefined || previous === undefined) return null;
  if (!previous) {
    if (!current) return <span className={`${styles.Pill} ${styles.PillNeutral}`}>No change</span>;
    return <span className={`${styles.Pill} ${styles.PillUp}`}>{`▲ New${short ? '' : ' this month'}`}</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  const up = pct >= 0;
  return (
    <span className={`${styles.Pill} ${up ? styles.PillUp : styles.PillDown}`}>
      {`${up ? '▲' : '▼'} ${Math.abs(pct)}%${short ? '' : ` ${suffix}`}`}
    </span>
  );
}

function StatCard({ title, value, icon, tone, footer, loading }) {
  return (
    <div className={`${styles.Card} ${styles.StatCard}`}>
      <div className={styles.StatTop}>
        <p className={styles.StatTitle}>{title}</p>
        <span className={`${styles.IconChip} ${styles[`Tone${tone}`]}`}>{icon}</span>
      </div>
      {loading ? (
        <SkeletonLine width="90px" />
      ) : (
        <>
          <p className={styles.StatValue}>{value}</p>
          {footer}
        </>
      )}
    </div>
  );
}

function OpsTile({ label, value, icon, alert, to, suffix }) {
  return (
    <Link to={to} className={`${styles.OpsTile} ${alert ? styles.OpsTileAlert : ''}`}>
      <span className={styles.OpsLabel}>
        {icon}
        {label}
      </span>
      <span className={styles.OpsValue}>
        {value}
        {suffix && <small>{suffix}</small>}
      </span>
    </Link>
  );
}

function Pipeline({ counts }) {
  return (
    <div className={styles.Pipeline}>
      {STAGE_META.map((stage, i) => (
        <div
          key={stage.status}
          className={`${styles.Stage} ${stage.region === 'uk' ? styles.StageUk : styles.StageGh}`}
        >
          <span className={styles.StageDot} />
          <span className={styles.StageBox}>
            <span className={styles.StageCount}>{counts[i] ?? 0}</span>
            <span className={styles.StageLabel}>{stage.label}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// Mobile stand-in for the Chart.js bar chart: one row per month with a
// revenue bar and a collected bar, so values stay legible on a phone.
function MobileRevenueBars({ labels, revenue, collected }) {
  const [showAll, setShowAll] = useState(false);
  const max = Math.max(...revenue, ...collected, 1);
  const start = showAll ? 0 : Math.max(labels.length - 6, 0);

  return (
    <div className={styles.MobileBars}>
      {labels.slice(start).map((label, idx) => {
        const i = start + idx;
        const rev = revenue[i] ?? 0;
        const col = collected[i] ?? 0;
        return (
          <div className={styles.MobileBarRow} key={label}>
            <span className={styles.MobileBarLabel}>{String(label).split(' ')[0]}</span>
            <div className={styles.MobileBarPair}>
              <div className={styles.MobileBarTrack}>
                <div className={styles.MobileBarFill} style={{ '--bar-w': `${Math.max((rev / max) * 100, 2)}%` }} />
              </div>
              <div className={styles.MobileBarTrack}>
                <div
                  className={`${styles.MobileBarFill} ${styles.MobileBarFillAlt}`}
                  style={{ '--bar-w': `${Math.max((col / max) * 100, 2)}%` }}
                />
              </div>
            </div>
            <span className={styles.MobileBarValue}>{formatCompactGBP(rev)}</span>
          </div>
        );
      })}
      {labels.length > 6 && (
        <button type="button" className={styles.TextButton} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show last 6 months' : `Show all ${labels.length} months`}
        </button>
      )}
    </div>
  );
}

function CollectionRing({ rate }) {
  const r = 44;
  const circumference = 2 * Math.PI * r;
  const dash = (Math.min(Math.max(rate, 0), 100) / 100) * circumference;
  return (
    <svg className={styles.Ring} viewBox="0 0 110 110" role="img" aria-label={`Collection rate ${rate}%`}>
      <circle cx="55" cy="55" r={r} className={styles.RingTrack} />
      <circle
        cx="55"
        cy="55"
        r={r}
        className={styles.RingValue}
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 55 55)"
      />
      <text x="55" y="58" textAnchor="middle" className={styles.RingText}>
        {`${rate}%`}
      </text>
      <text x="55" y="76" textAnchor="middle" className={styles.RingSub}>
        collected
      </text>
    </svg>
  );
}

function CardHead({ title, to, linkText = 'View all' }) {
  return (
    <div className={styles.CardHead}>
      <h2 className={styles.CardTitle}>{title}</h2>
      {to && (
        <Link to={to} className={styles.CardLink}>
          {linkText} <ArrowRight />
        </Link>
      )}
    </div>
  );
}

function Dashboard() {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { websiteData } = useWebsite();
  useSiteBranding('Dashboard', websiteData);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    axios
      .get(process.env.REACT_APP_API_URL + 'v1/dashboard/get-data', {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.status === 'SUCCESS') {
          setDashboardData(response.data.data);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [API_TOKEN]);

  const trends = dashboardData?.kpi_trends || {};
  const payments = dashboardData?.payments || {};
  const today = dashboardData?.today || {};
  const freight = dashboardData?.freight_split || {};
  const stageCounts = dashboardData?.doughnut_data || STAGE_META.map(() => 0);
  const bookings = dashboardData?.latest_bookings || [];
  const shipments = dashboardData?.active_shipments || [];
  const topOutstanding = payments.top_outstanding || [];

  const monthLabels = dashboardData?.bar_data?.labels || MONTH_LABELS;
  const revenueSeries = (dashboardData?.bar_data?.data || monthLabels.map(() => 0)).map(toNumber);
  const collectedSeries = (dashboardData?.bar_data?.collected || monthLabels.map(() => 0)).map(toNumber);

  const collected = toNumber(payments.collected);
  const outstanding = toNumber(payments.outstanding);
  const collectionRate = collected + outstanding > 0 ? Math.round((collected / (collected + outstanding)) * 100) : 0;
  const airCount = toNumber(freight.air);
  const seaCount = toNumber(freight.sea);
  const airPct = airCount + seaCount > 0 ? Math.round((airCount / (airCount + seaCount)) * 100) : 0;

  const totalRevenue = dashboardData?.total_revenue_raw ?? dashboardData?.total_revenue;

  const barData = {
    labels: monthLabels,
    datasets: [
      {
        label: 'Revenue',
        data: revenueSeries,
        backgroundColor: 'rgba(230, 79, 34, 0.85)',
        borderRadius: 4,
        maxBarThickness: 18,
      },
      {
        label: 'Collected',
        data: collectedSeries,
        backgroundColor: 'rgba(29, 158, 117, 0.85)',
        borderRadius: 4,
        maxBarThickness: 18,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: { boxWidth: 10, boxHeight: 10, font: { size: 12 } },
      },
      title: { display: false },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatGBP(ctx.parsed.y)}` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, maxRotation: 0, autoSkip: true },
      },
      y: {
        grid: { color: '#f1f1f1' },
        ticks: { font: { size: 11 }, callback: (v) => formatCompactGBP(v) },
      },
    },
  };

  const opsTiles = [
    { key: 'pickups', label: 'Pickups today', value: today.pickups_today ?? 0, icon: <MapPin />, to: '/booking/assign-drivers' },
    { key: 'unassigned', label: 'Unassigned pickups', value: today.unassigned_pickups ?? dashboardData?.new_bookings ?? 0, icon: <AlertTriangle />, to: '/booking/assign-drivers', needsAction: true },
    { key: 'awaiting', label: 'Awaiting delivery', value: today.awaiting_delivery_assign ?? 0, icon: <Package />, to: '/delivery/assign-to-driver', needsAction: true },
    { key: 'out', label: 'Out for delivery', value: today.out_for_delivery ?? 0, icon: <Truck />, to: '/delivery/driver-jobs' },
    { key: 'drivers', label: 'Drivers online', value: today.online_drivers ?? 0, suffix: ` / ${today.total_drivers ?? 0}`, icon: <User />, to: '/driver/list' },
  ].map((tile) => ({ ...tile, alert: tile.needsAction && tile.value > 0 }));

  // On phones the strip scrolls sideways, so put tiles that need action first.
  const orderedTiles = isMobile ? [...opsTiles].sort((a, b) => Number(b.alert) - Number(a.alert)) : opsTiles;

  return (
    <div className={styles.MainDiv}>
      <PageHeader title="Dashboard" />

      <div className={styles.MainCard}>
        {/* Greeting + quick actions */}
        <div className={styles.Greeting}>
          <div>
            <p className={styles.GreetingTitle}>{`${greeting()}${auth?.name ? `, ${auth.name.split(' ')[0]}` : ''}`}</p>
            <p className={styles.GreetingDate}>{moment().format('dddd, D MMMM YYYY')}</p>
          </div>
          <div className={styles.QuickActions}>
            <button type="button" className={`${styles.QuickBtn} ${styles.QuickBtnPrimary}`} onClick={() => navigate('/booking/add-booking')}>
              <Plus />
              <span>New booking</span>
            </button>
            <button type="button" className={styles.QuickBtn} onClick={() => navigate('/booking/assign-drivers')}>
              <Truck />
              <span>Assign drivers</span>
            </button>
            <button type="button" className={styles.QuickBtn} onClick={() => navigate('/delivery/receive-shipping')}>
              <Package />
              <span>Receive shipping</span>
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className={styles.StatsRow}>
          <StatCard
            title="Total bookings"
            value={toNumber(dashboardData?.total_bookings).toLocaleString('en-GB')}
            icon={<Calendar />}
            tone="Blue"
            loading={loading}
            footer={<TrendPill {...trends.bookings} short={isMobile} />}
          />
          <StatCard
            title="Revenue"
            value={isMobile ? formatCompactGBP(totalRevenue) : formatGBP(totalRevenue)}
            icon={<span className={styles.PoundIcon}>£</span>}
            tone="Orange"
            loading={loading}
            footer={<TrendPill {...trends.revenue} short={isMobile} />}
          />
          <StatCard
            title="Outstanding dues"
            value={isMobile ? formatCompactGBP(outstanding) : formatGBP(outstanding)}
            icon={<FileText />}
            tone="Amber"
            loading={loading}
            footer={
              <span className={`${styles.Pill} ${styles.PillWarn}`}>
                {`${payments.unpaid_count ?? 0} unpaid`}
              </span>
            }
          />
          <StatCard
            title="Customers"
            value={toNumber(dashboardData?.total_customers).toLocaleString('en-GB')}
            icon={<Users />}
            tone="Teal"
            loading={loading}
            footer={<TrendPill {...trends.customers} suffix="new vs last month" short={isMobile} />}
          />
        </div>

        {/* Today's operations */}
        <div className={styles.Card}>
          <div className={styles.CardBody}>
            <CardHead title="Today's operations" />
            {loading ? (
              <SkeletonBlock className={styles.SkeletonOps} />
            ) : (
              <div className={styles.OpsStrip}>
                {orderedTiles.map(({ key, needsAction, ...tile }) => (
                  <OpsTile key={key} {...tile} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Shipment pipeline */}
        <div className={styles.Card}>
          <div className={styles.CardBody}>
            <div className={styles.CardHead}>
              <h2 className={styles.CardTitle}>Shipment pipeline</h2>
              <span className={styles.Legend}>
                <span className={styles.LegendUk}>UK</span>
                <span className={styles.LegendGh}>Ghana</span>
              </span>
            </div>
            {loading ? <SkeletonBlock className={styles.SkeletonPipeline} /> : <Pipeline counts={stageCounts} />}
          </div>
        </div>

        <div className={styles.SplitRow}>
          {/* Revenue vs collected */}
          <div className={styles.Card}>
            <div className={styles.CardBody}>
              <CardHead title="Revenue vs collected" to="/report/payment-report" linkText="Report" />
              <div className={isMobile ? undefined : styles.BarChart}>
                {loading ? (
                  <SkeletonBlock className={styles.SkeletonChart} />
                ) : isMobile ? (
                  <MobileRevenueBars labels={monthLabels} revenue={revenueSeries} collected={collectedSeries} />
                ) : (
                  <Bar options={chartOptions} data={barData} />
                )}
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className={styles.Card}>
            <div className={styles.CardBody}>
              <CardHead title="Payments" />
              {loading ? (
                <SkeletonBlock className={styles.SkeletonRing} />
              ) : (
                <div className={styles.Payments}>
                  <CollectionRing rate={collectionRate} />
                  <div className={styles.PaymentFigures}>
                    <div className={styles.ListRow}>
                      <span className={styles.Muted}>Collected</span>
                      <span className={styles.Good}>{formatGBP(collected)}</span>
                    </div>
                    <div className={styles.ListRow}>
                      <span className={styles.Muted}>Outstanding</span>
                      <span className={styles.Bad}>{formatGBP(outstanding)}</span>
                    </div>
                    <div className={styles.ListRow}>
                      <span className={styles.Muted}>Unpaid bookings</span>
                      <span>{payments.unpaid_count ?? 0}</span>
                    </div>
                    <p className={styles.SplitTitle}>Air vs sea freight</p>
                    <div className={styles.SplitBar}>
                      <span className={styles.SplitAir} style={{ width: `${airPct}%` }} />
                      <span className={styles.SplitSea} style={{ width: `${100 - airPct}%` }} />
                    </div>
                    <div className={styles.SplitLegend}>
                      <span>{`Air ${airPct}% (${airCount})`}</span>
                      <span>{`Sea ${100 - airPct}% (${seaCount})`}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.SplitRow}>
          {/* Latest bookings */}
          <div className={styles.Card}>
            <div className={styles.CardBody}>
              <CardHead title="Latest bookings" to="/booking/list" />
              {loading ? (
                <SkeletonRows count={6} />
              ) : bookings.length === 0 ? (
                <p className={styles.EmptyState}>New bookings will show up here.</p>
              ) : (
                <div className={styles.BookingList}>
                  <div className={`${styles.BookingRow} ${styles.BookingHead}`}>
                    <span>Booking</span>
                    <span>Customer</span>
                    <span>Total</span>
                    <span>Status</span>
                  </div>
                  {bookings.map((item) => {
                    const stage = stageFor(item?.status);
                    const due = toNumber(item?.due_balance);
                    return (
                      <div className={styles.BookingRow} key={item?.id}>
                        <span className={styles.BookingId}>
                          {item?.booking_id}
                          <small>{moment(item?.created_at).format('DD/MM/YYYY')}</small>
                        </span>
                        <span className={styles.BookingCustomer}>{item?.customer_name}</span>
                        <span className={styles.BookingTotal}>
                          {formatGBP(item?.total)}
                          {due > 0 && <small className={styles.Bad}>{`Due ${formatGBP(due)}`}</small>}
                        </span>
                        <span className={styles.BookingStatus}>
                          <span
                            className={`${styles.Badge} ${
                              stage ? (stage.status === 7 ? styles.BadgeDone : stage.region === 'uk' ? styles.BadgeUk : styles.BadgeGh) : ''
                            }`}
                          >
                            {item?.status_text || stage?.label || '-'}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={styles.SideStack}>
            {/* Active shipments */}
            <div className={styles.Card}>
              <div className={styles.CardBody}>
                <CardHead title="Active shipments" to="/shipping/list" />
                {loading ? (
                  <SkeletonRows count={3} />
                ) : shipments.length === 0 ? (
                  <p className={styles.EmptyState}>No containers on the move right now.</p>
                ) : (
                  shipments.map((item) => {
                    const meta = SHIPMENT_STATUS[item?.status] || SHIPMENT_STATUS[0];
                    const eta = item?.eta_of_container || (item?.shipping_date && moment(item.shipping_date).format('DD MMM'));
                    return (
                      <div className={styles.ListRow} key={item?.id}>
                        <div className={styles.ListMain}>
                          <p>{item?.container_name || item?.reference_id}</p>
                          <small>{`${item?.eta_of_container ? 'ETA ' : 'Shipped '}${eta || '-'} · ${item?.no_of_invoice ?? 0} items`}</small>
                        </div>
                        <span className={`${styles.Badge} ${styles[`Tone${meta.tone}`]}`}>{meta.label}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Top outstanding */}
            <div className={styles.Card}>
              <div className={styles.CardBody}>
                <CardHead title="Top outstanding" to="/report/payment-report" linkText="Report" />
                {loading ? (
                  <SkeletonRows count={3} />
                ) : topOutstanding.length === 0 ? (
                  <p className={styles.EmptyState}>All bookings are paid up.</p>
                ) : (
                  topOutstanding.map((item) => (
                    <div className={styles.ListRow} key={item?.id}>
                      <div className={styles.ListMain}>
                        <p>{item?.customer_name || '-'}</p>
                        <small>{item?.booking_id}</small>
                      </div>
                      <span className={styles.Bad}>{formatGBP(item?.due_balance)}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withAdminAuth(Dashboard);
